import { useState } from 'react'
import { Card } from '@cmsgov/ds-medicare-gov'
import { ReactComponent as CaretIcon } from 'images/Icon_Caret.svg'

import { useTranslation } from 'react-i18next'

import classes from './AccordionCard.module.scss'
import { Button } from 'components/Button/Button'
import classNames from 'classnames'

export interface IAccordionCardProps {
  /**
   * Optional class name to add to the outer card
   */
  className?: string;
  /**
   * Text for the accordion item heading.
   */
  heading: React.ReactNode | string;
  children?: React.ReactNode;
  /**
   * When true, it will the initial value for accordion
   */
  defaultOpen?: boolean
  /**
   * If `true`, expands the accordion, otherwise collapse it.
   * Setting this prop enables control over the accordion.
   */
  open?: boolean;
  /**
   * Callback fired when the open/close state is changed.
   *
   */
  onChange?: () => void;
}

export const AccordionCard = ({
  className,
  heading,
  children,
  defaultOpen,
  open,
  onChange
}: IAccordionCardProps) => {
  /*********
   * STATE *
   *********/
  const isControlled = (typeof open !== 'undefined')
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const expanded = isControlled ? open : isOpen
  const handleClick = () => {
    if (isControlled) {
      if (onChange) onChange()
    } else {
      setIsOpen((prev) => !prev)
    }
  }

  const { t } = useTranslation('common')

  const toggleButton = () => {
    const closeLabel = () => {
      return (
        <>
          {t('closeDetails')}
          <CaretIcon
            className="my-c-caret--up my-c-caret--blue my-u-ml--6"
            aria-hidden
            focusable={false}
          />
        </>
      )
    }
    const openLabel = () => {
      return (
        <>
          {t('openDetails')}
          <CaretIcon
            className="my-c-caret--down my-c-caret--blue my-u-ml--6"
            aria-hidden
            focusable={false}
          />
        </>
      )
    }
    return (
        <Button
          data-testid="card-toggle-button"
          className="ds-c-button ds-c-button--ghost my-u-px--0 my-u-py--0"
          onClick={handleClick}
          aria-expanded={expanded}
          aria-label={
            // Should probably pass this in. Sometimes this may need context. "Open details for {X record}"
            expanded
              ? t('closeDetails')
              : t('openDetails')
          }
        >
          {expanded
            ? closeLabel()
            : openLabel()
          }
        </Button>
    )
  }

  return (
    <Card className={classNames(classes.accordionCard, className)}>
      <div className='ds-l-row ds-u-margin--0 ds-u-align-items--center'>
        <div className={`ds-l-lg-col--8 ds-l-sm-col--12 ds-u-padding--3  ${classes.rightDivider}`}>
        {heading}
        </div>
        <div className={`ds-l-lg-col--4 ds-l-sm-col--12 ds-u-padding--3 ds-u-text-align--center`}>
          {toggleButton()}
        </div>
      </div>
      { expanded &&
      <>
        <hr className='ds-u-margin--0' />
        <div className='ds-u-padding--3'>
        {children}
        </div>
      </>
      }
    </Card>
  )
}
